import { stageGroup } from "@/lib/contacts";
import type { ContactListFilters } from "@/lib/queries";
import { atLeast, type BoardCard, type ContactBoard, type Role } from "@/lib/types";

/**
 * The Opportunities board (ADR-078): which drop does what, the optimistic move, the source's words,
 * and the board's filters in the URL.
 *
 * A drop between two working or scheduled columns is a move (`POST /leads/{id}/stage`). A drop on an
 * exit column is never a move: an exit retires every list row and reaches the phone's other records,
 * so it comes from the outcome that put the lead there — the drop opens Set outcome. A card dragged
 * out of an exit is an admin's reopen, with a reason (ADR-076); Do not call is not reopened at all.
 * Core-api holds the same rule (`lifecycle.hand_move_problem`); this copy only keeps an impossible
 * drop from looking possible.
 */

/** Cards a column loads at a time. */
export const BOARD_PAGE = 25;
/** The most a column shows; past it, the contacts list filtered to the stage has the rest. */
export const BOARD_MAX = 100;

export type DropAction =
  | { kind: "move"; stage: string }
  | { kind: "schedule"; stage: string }
  | { kind: "outcome"; stage: string }
  | { kind: "reopen"; stage: string }
  | { kind: "refused"; reason: string };

/** What dropping a card of stage `from` on the column of stage `to` does. Null when it does nothing
 *  (dropped where it was). */
export function dropAction(
  card: Pick<BoardCard, "stage" | "dnc">,
  to: string,
  role: Role | undefined,
): DropAction | null {
  const from = card.stage;
  if (from === to) return null;
  if (stageGroup(to) === "exit") return { kind: "outcome", stage: to };
  // Leaving an exit is a reopen whatever the column; the reopen picks its stage.
  if (stageGroup(from) === "exit") {
    if (card.dnc || from === "dnc") {
      return { kind: "refused", reason: "Do not call is not reopened here: the number comes off the do-not-call list first." };
    }
    if (!atLeast(role, "admin")) return { kind: "refused", reason: "Only an admin can reopen a contact that has left." };
    return { kind: "reopen", stage: to };
  }
  // A scheduled stage says when the lead is called; the dialer waits for it.
  if (stageGroup(to) === "scheduled") return { kind: "schedule", stage: to };
  return { kind: "move", stage: to };
}

/** What the column under a dragged card says it will do, for its highlight. */
export function dropHint(action: DropAction | null): { tone: "move" | "ask" | "refused" | null; text: string | null } {
  if (!action) return { tone: null, text: null };
  switch (action.kind) {
    case "move":
      return { tone: "move", text: "Drop to move" };
    case "schedule":
      return { tone: "ask", text: "Drop to pick the time" };
    case "outcome":
      return { tone: "ask", text: "Drop to record the outcome" };
    case "reopen":
      return { tone: "ask", text: "Drop to reopen" };
    case "refused":
      return { tone: "refused", text: action.reason };
  }
}

/** The board with one card moved, as the server will have it: out of whichever column holds it
 *  (its total one less) and to the top of `to` (one more), in its new stage since `now`. A board
 *  that does not hold the card is returned as it is; one without the `to` column (a column's own
 *  page) only loses it. Never mutates. */
export function moveCard(board: ContactBoard, leadId: string, to: string, now: string): ContactBoard {
  const card = board.columns.flatMap((c) => c.cards).find((c) => c.id === leadId);
  if (!card || card.stage === to) return board;
  const moved: BoardCard = { ...card, stage: to, stage_since: now, last_activity_at: now };
  return {
    ...board,
    columns: board.columns.map((column) => {
      if (column.stage === card.stage) {
        return {
          ...column,
          total: Math.max(0, column.total - 1),
          cards: column.cards.filter((c) => c.id !== leadId),
        };
      }
      if (column.stage === to) {
        return { ...column, total: column.total + 1, cards: [moved, ...column.cards] };
      }
      return column;
    }),
  };
}

// --- a scheduled stage's time ----------------------------------------------------------------------

/** How far ahead a scheduled stage's time may be set: core-api's `LONGEST_SCHEDULE`. */
export const LONGEST_SCHEDULE_DAYS = 180;

/** A `datetime-local` value (the reader's own clock, to the minute) for a `Date`. */
export function localInputValue(when: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}T${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

/** What the time picker opens on: tomorrow at 10 am, the reader's time. */
export function defaultScheduleTime(now: Date): string {
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 10, 0);
  return localInputValue(tomorrow);
}

/** Why a picked time will not do, or null: core-api's rule (`hand_move_time_problem`), said first. */
export function scheduleTimeProblem(value: string, now: Date): string | null {
  if (!value) return "Pick the day and time to call them.";
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return "That is not a time.";
  if (when.getTime() <= now.getTime()) return "The time is in the past.";
  if (when.getTime() > now.getTime() + LONGEST_SCHEDULE_DAYS * 86_400_000) {
    return `Pick a time within ${LONGEST_SCHEDULE_DAYS} days.`;
  }
  return null;
}

/** A card's source in a person's words, from `leads.source` (`facebook:mva-texas`, `csv:7`,
 *  `console`, a trigger's own name). The channel is what the floor asks ("where did they come
 *  from?"); the rest of the value is the detail. */
export function sourceText(source: string | null | undefined): { label: string; detail: string | null } {
  const raw = (source ?? "").trim();
  if (!raw) return { label: "Unknown", detail: null };
  const [head, ...rest] = raw.split(":");
  const key = head!.toLowerCase();
  const detail = rest.join(":").trim() || null;
  const named = SOURCES[key];
  if (named) return { label: named, detail: key === "csv" ? null : detail };
  const words = key.replace(/[-_.]+/g, " ").trim();
  return { label: words ? words[0]!.toUpperCase() + words.slice(1) : raw, detail };
}

const SOURCES: Record<string, string> = {
  facebook: "Facebook",
  fb: "Facebook",
  instagram: "Instagram",
  ig: "Instagram",
  tiktok: "TikTok",
  google: "Google",
  youtube: "YouTube",
  ads: "Ads",
  website: "Website",
  web: "Website",
  ghl: "GoHighLevel",
  zapier: "Zapier",
  csv: "CSV import",
  console: "Added by hand",
  import: "Import",
};

/** The CRM tags a lead arrived with, as the board's card has them (core-api's `tags_of`): GHL's
 *  `ghl_tags`, a list or one comma-joined string, blanks and repeats dropped, in their order. */
export function leadTags(attributes: Record<string, unknown> | null | undefined): string[] {
  const raw = attributes?.ghl_tags;
  const values = typeof raw === "string" ? raw.split(",") : Array.isArray(raw) ? raw : [];
  return [...new Set(values.map((v) => String(v).trim()).filter(Boolean))];
}

/** The tags a card has room for, and how many it leaves out. */
export function visibleTags(tags: readonly string[], room = 3): { shown: string[]; more: number } {
  return { shown: tags.slice(0, room), more: Math.max(0, tags.length - room) };
}

// --- the board's filters, in the URL -------------------------------------------------------------

/** `all` is every firm; `none` the leads no firm tag resolves; else a firm's code. */
export interface BoardFilters {
  firm: string;
  q: string;
  sort: BoardSort;
}

export const BOARD_SORTS = ["newest", "oldest", "name"] as const;
export type BoardSort = (typeof BOARD_SORTS)[number];

export const BOARD_SORT_TEXT: Record<BoardSort, string> = {
  newest: "Newest first",
  oldest: "Oldest first",
  name: "Name A–Z",
};

export function boardFiltersFromParams(params: URLSearchParams): BoardFilters {
  const sort = params.get("sort") ?? "";
  return {
    firm: params.get("firm") || "all",
    q: params.get("q") ?? "",
    sort: (BOARD_SORTS as readonly string[]).includes(sort) ? (sort as BoardSort) : "newest",
  };
}

/** The params for `filters`, keeping whatever else the URL holds (the open lead). */
export function withBoardFilters(params: URLSearchParams, filters: BoardFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (key: string, value: string, fallback: string) => {
    if (value && value !== fallback) next.set(key, value);
    else next.delete(key);
  };
  set("firm", filters.firm, "all");
  set("q", filters.q.trim(), "");
  set("sort", filters.sort, "newest");
  return next;
}

/** What `GET /leads/board` is asked. */
export function boardApiFilters(filters: BoardFilters): ContactListFilters {
  const out: ContactListFilters = {};
  if (filters.firm !== "all") out.firm = filters.firm;
  if (filters.q.trim()) out.q = filters.q.trim();
  if (filters.sort === "name") {
    out.sort = "name";
    out.order = "asc";
  } else {
    out.sort = "created";
    out.order = filters.sort === "oldest" ? "asc" : "desc";
  }
  return out;
}

/**
 * The Edit opportunity form's fields, in its order: GoHighLevel's "Manage fields" hides the ones a
 * person does not use. A required field is always shown.
 */
export const FORM_FIELDS = [
  { id: "contact_name", label: "Primary contact name", section: "contact", required: true },
  { id: "email", label: "Primary email", section: "contact", required: false },
  { id: "phone", label: "Primary phone", section: "contact", required: false },
  { id: "additional_contacts", label: "Additional contacts", section: "contact", required: false },
  { id: "opportunity_name", label: "Opportunity name", section: "opportunity", required: true },
  { id: "pipeline", label: "Pipeline", section: "opportunity", required: true },
  { id: "stage", label: "Stage", section: "opportunity", required: true },
  { id: "status", label: "Status", section: "opportunity", required: false },
  { id: "value", label: "Value", section: "opportunity", required: false },
  { id: "owner", label: "Owner", section: "opportunity", required: false },
  { id: "followers", label: "Followers", section: "opportunity", required: false },
  { id: "business_name", label: "Business name", section: "opportunity", required: false },
  { id: "source", label: "Source", section: "opportunity", required: false },
] as const;
export type FormField = (typeof FORM_FIELDS)[number]["id"];

/** The `ui_prefs` key the fields a person hid are kept under. */
export const HIDDEN_FIELDS_PREF = "opportunity_form_hidden";

/** The fields a person hid, from their `ui_prefs`: only ones the form has and may hide. */
export function readHiddenFields(prefs: Record<string, unknown> | undefined): FormField[] {
  const raw = prefs?.[HIDDEN_FIELDS_PREF];
  if (!Array.isArray(raw)) return [];
  return FORM_FIELDS.filter((f) => !f.required && raw.includes(f.id)).map((f) => f.id);
}

/** Shows a hidden field or hides a shown one. A required field stays shown. */
export function toggleHidden(hidden: readonly FormField[], field: FormField): FormField[] {
  if (FORM_FIELDS.find((f) => f.id === field)?.required) return [...hidden];
  const next = hidden.includes(field) ? hidden.filter((f) => f !== field) : [...hidden, field];
  return FORM_FIELDS.filter((f) => next.includes(f.id)).map((f) => f.id);
}
